import React, { useCallback, useEffect, useRef, useState } from 'react';

import { dismiss, getToasts, subscribe } from './toastStore';
import './Toast.css';

/** Must match the exit animation in Toast.css. */
const EXIT_MS = 200;

const Glyph = ({ kind }) => {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  if (kind === 'success') {
    return (
      <svg {...common}>
        <path d="m4 12.5 5 5L20 6.5" />
      </svg>
    );
  }
  if (kind === 'error') {
    return (
      <svg {...common}>
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    );
  }
  if (kind === 'warning') {
    return (
      <svg {...common}>
        <path d="M12 4v10" />
        <path d="M12 19.5h.01" />
      </svg>
    );
  }
  if (kind === 'loading') {
    return <span className="syn-toast-spinner" aria-hidden="true" />;
  }
  return (
    <svg {...common}>
      <path d="M12 10.5v9" />
      <path d="M12 5.5h.01" />
    </svg>
  );
};

/**
 * The app's only toast surface — bottom right, newest nearest the corner.
 *
 * Mounted once, in App. It renders from `toastStore` rather than props so the
 * non-React callers (interceptor, middlewares) reach the same stack.
 *
 * Behaviour the antd version did not have: a dismiss control, a life bar that
 * shows how long is left, pause-on-hover (and on keyboard focus, so a toast
 * cannot expire while someone is tabbed into its close button), repeat
 * collapsing, and an exit animation instead of a disappearance.
 */
const Toaster = () => {
  // Local mirror of the store. Toasts the store has dropped are kept here for
  // one exit animation, flagged `exiting`, then released.
  const [items, setItems] = useState(() => getToasts().map((t) => ({ ...t, exiting: false })));
  const [paused, setPaused] = useState(false);

  const timers = useRef(new Map());
  const pausedRef = useRef(false);

  useEffect(
    () =>
      subscribe((store) => {
        setItems((previous) => {
          const live = new Map(store.map((toast) => [toast.id, toast]));
          const merged = [];

          // Walk the rendered order first so a toast never jumps position when
          // one above it leaves.
          for (const item of previous) {
            const fresh = live.get(item.id);
            if (fresh) {
              merged.push({ ...fresh, exiting: false });
              live.delete(item.id);
            } else {
              merged.push(item.exiting ? item : { ...item, exiting: true });
            }
          }
          for (const toast of live.values()) merged.push({ ...toast, exiting: false });

          return merged;
        });
      }),
    []
  );

  // Release exited toasts once their animation has run.
  useEffect(() => {
    const leaving = items.filter((item) => item.exiting);
    if (!leaving.length) return undefined;

    const handles = leaving.map((item) =>
      setTimeout(
        () => setItems((prev) => prev.filter((p) => p.id !== item.id || !p.exiting)),
        EXIT_MS
      )
    );
    return () => handles.forEach(clearTimeout);
  }, [items]);

  /**
   * Auto-dismiss timers, one per toast, keyed on `id:revision` so a refreshed
   * toast restarts its clock exactly as its life bar does.
   *
   * `paused` is read through a ref: including it in the dependencies would
   * re-run this and, because the stamps still match, silently skip the restart.
   */
  useEffect(() => {
    const map = timers.current;

    for (const toast of items) {
      if (toast.exiting) continue;

      const stamp = `${toast.id}:${toast.revision}`;
      const existing = map.get(toast.id);
      if (existing?.stamp === stamp) continue;
      if (existing?.handle) clearTimeout(existing.handle);

      if (!toast.duration) {
        map.set(toast.id, { stamp, handle: null, remaining: 0, endsAt: 0 });
        continue;
      }

      const ms = toast.duration * 1000;
      map.set(toast.id, {
        stamp,
        remaining: ms,
        endsAt: Date.now() + ms,
        handle: pausedRef.current ? null : setTimeout(() => dismiss(toast.id), ms),
      });
    }

    for (const [id, timer] of map) {
      if (items.some((item) => item.id === id && !item.exiting)) continue;
      if (timer.handle) clearTimeout(timer.handle);
      map.delete(id);
    }
  }, [items]);

  // Every pending timer is cleared on unmount, including the exit timeouts held
  // by the effect above — a toast outliving its container would call `dismiss`
  // into a store nothing is listening to.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) if (timer.handle) clearTimeout(timer.handle);
      map.clear();
    };
  }, []);

  const pause = useCallback(() => {
    if (pausedRef.current) return;
    pausedRef.current = true;
    setPaused(true);

    for (const timer of timers.current.values()) {
      if (!timer.handle) continue;
      clearTimeout(timer.handle);
      timer.handle = null;
      timer.remaining = Math.max(0, timer.endsAt - Date.now());
    }
  }, []);

  const resume = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    setPaused(false);

    for (const [id, timer] of timers.current) {
      if (timer.remaining <= 0) continue;
      timer.endsAt = Date.now() + timer.remaining;
      timer.handle = setTimeout(() => dismiss(id), timer.remaining);
    }
  }, []);

  // The container is always mounted, empty or not: a live region that appears in
  // the same frame as its first child is frequently not announced at all, so
  // returning null here would cost screen-reader users the first toast.
  return (
    <div
      className={`syn-toaster${paused ? ' is-paused' : ''}`}
      // polite, not assertive: these interrupt nothing, and an assertive region
      // cuts off whatever the screen reader is mid-sentence on.
      aria-live="polite"
      aria-atomic="false"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      {items.map((toast) => (
        <div
          key={toast.id}
          className={`syn-toast is-${toast.kind}${toast.exiting ? ' is-leaving' : ''}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          style={{ '--toast-life': `${toast.duration}s` }}
        >
          <span className="syn-toast-glyph">
            <Glyph kind={toast.kind} />
          </span>

          <div className="syn-toast-body">
            {toast.title && <div className="syn-toast-title">{toast.title}</div>}
            <div className="syn-toast-text">{toast.content}</div>
          </div>

          {toast.repeat > 1 && (
            <span className="syn-toast-repeat" title={`Repeated ${toast.repeat} times`}>
              {toast.repeat}
            </span>
          )}

          <button
            type="button"
            className="syn-toast-close"
            aria-label="Dismiss notification"
            onClick={() => dismiss(toast.id)}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>

          {toast.duration > 0 && (
            // Remounted on every revision so the CSS animation restarts.
            <span key={`life-${toast.revision}`} className="syn-toast-life" aria-hidden="true" />
          )}
        </div>
      ))}
    </div>
  );
};

export default Toaster;
